/**
 * Safe JSON parsing utility with error handling and validation
 * Prevents application crashes from corrupted localStorage/sessionStorage data
 */

/**
 * Safely parse JSON with error handling
 * @param {string} jsonString - The JSON string to parse
 * @param {*} defaultValue - Value to return if parsing fails (default: null)
 * @param {Object} options - Additional options
 * @param {Function} options.validator - Optional validator function to check parsed data structure
 * @param {boolean} options.logErrors - Whether to log parsing errors (default: true in dev, false in prod)
 * @returns {*} Parsed object or defaultValue
 */
export function safeJsonParse(jsonString, defaultValue = null, options = {}) {
  const { validator, logErrors = import.meta.env.DEV } = options;

  // Handle null/undefined/empty inputs
  if (!jsonString || typeof jsonString !== 'string') {
    return defaultValue;
  }

  try {
    const parsed = JSON.parse(jsonString);

    // Run optional validator
    if (validator && typeof validator === 'function') {
      if (!validator(parsed)) {
        if (logErrors) {
          console.warn('[safeJsonParse] Validation failed for parsed data:', parsed);
        }
        return defaultValue;
      }
    }

    return parsed;
  } catch (error) {
    if (logErrors) {
      console.warn('[safeJsonParse] Failed to parse JSON:', error.message);
      console.warn('[safeJsonParse] Invalid JSON string:', jsonString.substring(0, 100));
    }
    return defaultValue;
  }
}

/**
 * Safely stringify an object to JSON
 * @param {*} value - Value to stringify
 * @param {*} defaultValue - Value to return if stringification fails (default: null)
 * @param {Object} options - Additional options
 * @param {number} options.space - Number of spaces for indentation (default: 0)
 * @param {boolean} options.logErrors - Whether to log errors (default: true in dev)
 * @returns {string|*} JSON string or defaultValue
 */
export function safeJsonStringify(value, defaultValue = null, options = {}) {
  const { space = 0, logErrors = import.meta.env.DEV } = options;

  try {
    return JSON.stringify(value, null, space);
  } catch (error) {
    if (logErrors) {
      console.warn('[safeJsonStringify] Failed to stringify value:', error.message);
    }
    return defaultValue;
  }
}

/**
 * Safe localStorage getter with JSON parsing
 * @param {string} key - localStorage key
 * @param {*} defaultValue - Default value if key doesn't exist or parsing fails
 * @param {Function} validator - Optional validator function
 * @returns {*} Parsed value or defaultValue
 */
export function getLocalStorage(key, defaultValue = null, validator = null) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaultValue;
    return safeJsonParse(raw, defaultValue, { validator });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[getLocalStorage] Error accessing localStorage key "${key}":`, error.message);
    }
    return defaultValue;
  }
}

/**
 * Safe localStorage setter with JSON stringification
 * @param {string} key - localStorage key
 * @param {*} value - Value to store
 * @returns {boolean} true if successful, false otherwise
 */
export function setLocalStorage(key, value) {
  try {
    const jsonString = safeJsonStringify(value);
    if (jsonString === null) return false;
    localStorage.setItem(key, jsonString);
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[setLocalStorage] Error setting localStorage key "${key}":`, error.message);
    }
    return false;
  }
}

/**
 * Safe sessionStorage getter with JSON parsing
 * @param {string} key - sessionStorage key
 * @param {*} defaultValue - Default value if key doesn't exist or parsing fails
 * @param {Function} validator - Optional validator function
 * @returns {*} Parsed value or defaultValue
 */
export function getSessionStorage(key, defaultValue = null, validator = null) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return defaultValue;
    return safeJsonParse(raw, defaultValue, { validator });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[getSessionStorage] Error accessing sessionStorage key "${key}":`, error.message);
    }
    return defaultValue;
  }
}

/**
 * Safe sessionStorage setter with JSON stringification
 * @param {string} key - sessionStorage key
 * @param {*} value - Value to store
 * @returns {boolean} true if successful, false otherwise
 */
export function setSessionStorage(key, value) {
  try {
    const jsonString = safeJsonStringify(value);
    if (jsonString === null) return false;
    sessionStorage.setItem(key, jsonString);
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[setSessionStorage] Error setting sessionStorage key "${key}":`, error.message);
    }
    return false;
  }
}

/**
 * Common validators for typical data structures
 */
export const validators = {
  isObject: value => value !== null && typeof value === 'object' && !Array.isArray(value),
  isArray: value => Array.isArray(value),
  isString: value => typeof value === 'string',
  isNumber: value => typeof value === 'number' && !isNaN(value),
  isBoolean: value => typeof value === 'boolean',
  hasKeys: (...keys) => value =>
    validators.isObject(value) && keys.every(key => key in value),
};

// Example usage:
// const user = safeJsonParse(rawJson, { name: 'Guest' }, {
//   validator: validators.hasKeys('name', 'email')
// });
